import type { PDFPageProxy } from 'pdfjs-dist';
import type { CanvasRenderOptions, ScaleOptions } from '../types';

// 성능 최적화 상수
const DEFAULT_RENDER_SCALE = 1.0;
const DEFAULT_MAX_DPI_SCALE = 2.0;
const DEFAULT_MAX_CANVAS_SIZE = 4096;

/**
 * GPU 가속이 활성화된 Canvas Context 생성
 */
export const enableGPUAcceleration = (canvas: HTMLCanvasElement): CanvasRenderingContext2D | null => {
    const ctx = canvas.getContext('2d', {
        alpha: false,
        desynchronized: true,
        willReadFrequently: false,
    });

    if (!ctx) return null;

    // GPU 가속을 위한 CSS 설정
    Object.assign(canvas.style, {
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform',
    });

    return ctx;
};

/**
 * 최적화된 Canvas 생성
 */
export const createOptimizedCanvas = (width: number, height: number, options: CanvasRenderOptions = {}): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } | null => {
    const { enableGPU = true, maxCanvasSize = DEFAULT_MAX_CANVAS_SIZE } = options;

    const canvas = document.createElement('canvas');

    // 캔버스 크기 제한
    canvas.width = Math.min(Math.floor(width), maxCanvasSize);
    canvas.height = Math.min(Math.floor(height), maxCanvasSize);

    // Context 생성
    const context = enableGPU ? enableGPUAcceleration(canvas) : canvas.getContext('2d');

    if (!context) return null;

    return { canvas, context };
};

/**
 * 스케일 계산 (부모 컨테이너에 맞춤)
 */
export const calculateScale = (nativeWidth: number, containerWidth: number, options: ScaleOptions = {}): number => {
    const { renderScale = DEFAULT_RENDER_SCALE } = options;
    return (containerWidth / nativeWidth) * renderScale;
};

/**
 * DPI를 고려한 Canvas 스케일 계산
 */
export const calculateCanvasScale = (devicePixelRatio: number = window.devicePixelRatio || 1, maxDpiScale: number = DEFAULT_MAX_DPI_SCALE): number => {
    return Math.min(devicePixelRatio * 1.5, maxDpiScale);
};

/**
 * Canvas 크기 및 스타일 설정
 */
export const setupCanvasSize = (canvas: HTMLCanvasElement, scaledWidth: number, scaledHeight: number, canvasScale: number, maxCanvasSize: number = DEFAULT_MAX_CANVAS_SIZE): void => {
    const optimizedHeight = Math.min(Math.floor(scaledHeight * canvasScale), maxCanvasSize);
    const optimizedWidth = Math.min(Math.floor(scaledWidth * canvasScale), maxCanvasSize);

    canvas.height = optimizedHeight;
    canvas.width = optimizedWidth;
    canvas.style.height = `${Math.floor(scaledHeight)}px`;
    canvas.style.width = `${Math.floor(scaledWidth)}px`;
    canvas.style.backgroundColor = 'white';
};

/**
 * PDF 페이지를 Canvas에 렌더링
 */
export const renderPdfPage = async (page: PDFPageProxy, canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, scale: number, options: CanvasRenderOptions = {}): Promise<void> => {
    const { background = 'white' } = options;

    const viewport = page.getViewport({ scale });

    await page.render({
        canvasContext: context,
        viewport,
        background,
        intent: 'display',
    }).promise;
};

/**
 * 요소의 크기 설정 유틸리티
 */
export const setElementSize = (el: HTMLElement | null, width: number, height: number): void => {
    if (!el) return;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
};
